/**
 * What one civilisation thinks of another, and why (ADR-0060).
 *
 * Attitude is a sum anyone can read: what it remembers (wars declared, cities taken,
 * deals made, demands refused — each fading over time), what is true now (war, a
 * shared enemy, the same government, open borders, an alliance, a border pressing on
 * its own), and the other's reputation for keeping its word. The player is shown every
 * term, so an attitude is never a mystery and always says what would change it.
 */

import type { Content } from '../../../content/schema/index.js';
import { governmentOf, inAnarchy } from '../government/index.js';
import { neighborsByDirection, tileCount } from '../state/grid.js';
import type { GameState, Id } from '../state/types.js';
import { atWar, enemiesOf } from './index.js';
import { memoriesOf, memoryValue } from './memory.js';
import { relationOf } from './relation.js';

export type AttitudeBand = Content['rules']['diplomacy']['attitude']['bands'][number];

export interface AttitudeReason {
  label: string;
  value: number;
}

export interface Attitude {
  score: number;
  band: AttitudeBand;
  /** Every term that counts, largest first. */
  reasons: AttitudeReason[];
}

/** Tiles where `about`'s ground touches `who`'s: how hard two borders press on each other. */
export function frontier(state: GameState, who: Id, about: Id): number {
  const map = state.map;
  const owner = map.owner;
  const near = new Int32Array(6);
  let count = 0;
  const n = tileCount(map);
  for (let i = 0; i < n; i++) {
    if (owner[i] !== about) continue;
    neighborsByDirection(map, i, near);
    for (let d = 0; d < 6; d++) {
      const j = near[d]!;
      if (j >= 0 && owner[j] === who) {
        count++;
        break;
      }
    }
  }
  return count;
}

export function bandFor(content: Content, score: number): AttitudeBand {
  const bands = content.rules.diplomacy.attitude.bands;
  for (const band of bands) if (band.below !== null && score < band.below) return band;
  return bands[bands.length - 1]!;
}

/** What `who` thinks of `about`. */
export function attitude(state: GameState, content: Content, who: Id, about: Id): Attitude {
  const rules = content.rules.diplomacy;
  const terms = rules.attitude;
  const reasons: AttitudeReason[] = [];
  const add = (label: string, value: number): void => {
    if (Math.abs(value) >= 0.5) reasons.push({ label, value: Math.round(value) });
  };

  // Memories, summed by kind and capped where the rules say so.
  const byKind = new Map<string, number>();
  for (const memory of memoriesOf(state, who, about)) {
    byKind.set(memory.kind, (byKind.get(memory.kind) ?? 0) + memoryValue(content, memory, state.meta.turn));
  }
  for (const [kind, raw] of byKind) {
    const rule = rules.memories[kind]!;
    const value = rule.max === undefined ? raw : Math.max(-rule.max, Math.min(rule.max, raw));
    add(rule.label, value);
  }

  // What is true now.
  if (atWar(state, who, about)) add('we are at war', terms.atWar);
  const theirs = new Set(enemiesOf(state, about));
  const shared = enemiesOf(state, who).filter((e) => theirs.has(e)).length;
  if (shared > 0) add(shared === 1 ? 'we share an enemy' : 'we share enemies', shared * terms.sharedEnemy);
  const ours = state.civs[who];
  const other = state.civs[about];
  if (ours && other && !inAnarchy(ours) && !inAnarchy(other) && ours.government === other.government) {
    add(`we are both ${governmentOf(content, ours).name === 'Despotism' ? 'despotisms' : governmentOf(content, ours).name.toLowerCase() + 's'}`, terms.sameGovernment);
  }
  const relation = relationOf(state, who, about);
  if (relation?.openBorders) add('our borders are open to each other', terms.openBorders);
  if (relation && relation.alliances.length > 0) add('we are allies', terms.alliance);
  const pressure = frontier(state, who, about);
  if (pressure > 0) add('their border presses on ours', Math.max(terms.borderPressureMax, pressure * terms.borderPressurePerTile));
  if (other && other.reputation < 0) add('they have broken their word', other.reputation * terms.reputationWeight);

  reasons.sort((p, q) => Math.abs(q.value) - Math.abs(p.value) || p.label.localeCompare(q.label));
  const score = reasons.reduce((sum, r) => sum + r.value, 0);
  return { score, band: bandFor(content, score), reasons };
}
